<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import SiteFooter from "../components/SiteFooter.vue";
import { runtimeConfig } from "../runtime-config";
import {
  defaultAuthProviderSettings,
  fetchAuthProviderSettings,
  fetchWechatEmbeddedLoginConfig,
  resolveLoginOptions,
  resolveProviderStartUrl,
  type AuthProviderSettings,
  type LoginProviderOption,
  type WechatEmbeddedLoginConfig
} from "../login-options";
import { useAuthStore } from "../stores/auth";
import { usePetGenerationStore } from "../stores/petGeneration";

const router = useRouter();
const auth = useAuthStore();
const generation = usePetGenerationStore();
const { t } = useI18n();
const errorMessage = ref("");
const providerSettings = ref<AuthProviderSettings>(defaultAuthProviderSettings);
const providerLoading = ref(true);
const loginOptions = computed(() => resolveLoginOptions(providerSettings.value, runtimeConfig.market));
const wechatLoginCompleting = ref(false);
let wechatFramePollTimer: number | undefined;
let wechatRenderKey = 0;

const wechatProvider = computed(() => loginOptions.value.providers.find((provider) => provider.key === "wechat") ?? null);
const buttonProviders = computed(() => loginOptions.value.providers.filter((provider) => provider.key !== "wechat"));
const wechatContainerId = "wechat_login_container";

onMounted(loadAuthProviders);
onBeforeUnmount(stopWechatFramePolling);

watch(
  wechatProvider,
  (provider) => {
    if (provider) {
      void renderWechatLogin();
      return;
    }

    stopWechatFramePolling();
  },
  { immediate: true }
);

async function loadAuthProviders() {
  providerLoading.value = true;
  providerSettings.value = await fetchAuthProviderSettings();
  providerLoading.value = false;
  if (wechatProvider.value) {
    await renderWechatLogin();
  }
}

function startExternalLogin(provider: LoginProviderOption) {
  const target = new URL(resolveProviderStartUrl(provider.startUrl, runtimeConfig.apiBaseUrl));
  target.searchParams.set("returnUrl", `${window.location.origin}${runtimeConfig.market.generatePath}`);
  window.location.href = target.toString();
}

function startWechatFramePolling() {
  stopWechatFramePolling();
  wechatFramePollTimer = window.setInterval(() => {
    void tryCompleteWechatLoginFromFrame();
  }, 900);
}

function stopWechatFramePolling() {
  if (wechatFramePollTimer !== undefined) {
    window.clearInterval(wechatFramePollTimer);
    wechatFramePollTimer = undefined;
  }
}

async function renderWechatLogin() {
  const renderKey = ++wechatRenderKey;
  errorMessage.value = "";
  try {
    await nextTick();
    const container = document.getElementById(wechatContainerId);
    if (!container) {
      return;
    }

    container.innerHTML = "";
    const returnUrl = `${window.location.origin}/wechat-login-bridge`;
    const [config] = await Promise.all([
      fetchWechatEmbeddedLoginConfig(returnUrl),
      loadWechatLoginScript()
    ]);
    if (renderKey !== wechatRenderKey) {
      return;
    }

    createWechatLogin(config);
    startWechatFramePolling();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : t("login.loginFailed");
  }
}

async function tryCompleteWechatLoginFromFrame() {
  if (wechatLoginCompleting.value) {
    return;
  }

  const tokenFromStorage = localStorage.getItem("pet.auth.token")?.trim() ?? "";
  const tokenFromFrame = readWechatFrameToken();
  const token = tokenFromFrame || tokenFromStorage;
  if (!token) {
    return;
  }

  wechatLoginCompleting.value = true;
  stopWechatFramePolling();
  errorMessage.value = "";
  try {
    auth.acceptExternalToken(token);
    const restored = await auth.restoreSession();
    if (!restored) {
      throw new Error(t("login.loginFailed"));
    }
    if (auth.quota) {
      generation.quota = {
        generationCredits: auth.quota.generationCredits,
        petLimit: auth.quota.petLimit,
        petCount: auth.quota.petCount
      };
    }
    await generation.refreshAccountState();
    await router.push(runtimeConfig.market.generatePath);
  } catch (error) {
    auth.logout();
    errorMessage.value = error instanceof Error ? error.message : t("login.loginFailed");
    startWechatFramePolling();
  } finally {
    wechatLoginCompleting.value = false;
  }
}

function readWechatFrameToken() {
  const frameWindow = document.querySelector<HTMLIFrameElement>(`#${wechatContainerId} iframe`)?.contentWindow;
  if (!frameWindow) {
    return "";
  }

  try {
    const url = new URL(frameWindow.location.href);
    return url.searchParams.get("externalToken")?.trim() ?? "";
  } catch {
    return "";
  }
}

function loadWechatLoginScript() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  if (typeof window.WxLogin === "function") {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-wechat-login="true"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(t("login.wechatLoadFailed"))), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js";
    script.async = true;
    script.dataset.wechatLogin = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(t("login.wechatLoadFailed")));
    document.head.appendChild(script);
  });
}

function createWechatLogin(config: WechatEmbeddedLoginConfig) {
  if (typeof window.WxLogin !== "function") {
    throw new Error(t("login.wechatLoadFailed"));
  }

  window.WxLogin({
    self_redirect: true,
    id: wechatContainerId,
    appid: config.appId,
    scope: config.scope,
    redirect_uri: encodeURIComponent(config.redirectUri),
    state: config.state,
    style: config.style || "black",
    fast_login: 1,
    color_scheme: config.colorScheme || "light",
    onReady: (isReady: boolean) => {
      if (!isReady) {
        errorMessage.value = t("login.wechatLoadFailed");
      }
    },
    onQRcodeReady: () => {
      errorMessage.value = "";
    }
  });
}
</script>

<script lang="ts">
declare global {
  interface Window {
    WxLogin?: (options: {
      self_redirect?: boolean;
      id: string;
      appid: string;
      scope: string;
      redirect_uri: string;
      state?: string;
      style?: string;
      href?: string;
      stylelite?: number;
      fast_login?: number;
      color_scheme?: "light" | "dark" | "auto";
      onReady?: (isReady: boolean) => void;
      onQRcodeReady?: () => void;
    }) => void;
  }
}
</script>

<template>
  <main class="login-shell">
    <div class="login-main">
      <section class="login-visual" :aria-label="t('login.visualAria')">
        <div class="brand-mark">
          <img class="brand-logo" src="/brand-logo.png" alt="" aria-hidden="true" />
          <span>{{ t("common.brand") }}</span>
        </div>
        <img src="/marketing/pet-companion-hero.png" :alt="t('login.heroAlt')" class="hero-image" />
        <div class="visual-copy">
          <h1>{{ t("login.heroTitle") }}</h1>
          <p>{{ t("login.heroText") }}</p>
        </div>
      </section>

      <section class="login-panel" aria-labelledby="loginTitle">
        <div class="panel-card login-card">
          <p class="eyebrow">{{ t("login.welcome") }}</p>
          <h2 id="loginTitle">{{ t("login.title") }}</h2>
          <p class="muted">{{ t("login.firstUse") }}</p>

          <!-- 邮箱密码登录入口暂时下线；登录页只展示当前市场配置的第三方登录方式。
          <form v-if="!providerLoading && loginOptions.showPasswordLogin" class="login-form" @submit.prevent="submitLogin">
            <label>
              <span>{{ t("login.email") }}</span>
              <input v-model="email" type="email" autocomplete="email" placeholder="name@example.com" />
            </label>
            <label>
              <span>{{ t("login.password") }}</span>
              <input v-model="password" type="password" autocomplete="current-password" :placeholder="t('login.passwordPlaceholder')" />
            </label>
            <p v-if="errorMessage" class="form-error">{{ errorMessage }}</p>
            <button class="primary-button full-button" type="submit" :disabled="loading">
              <span>{{ loading ? t("login.loggingIn") : t("login.login") }}</span>
            </button>
          </form>
          -->

          <div v-if="wechatProvider" class="wechat-login-card">
            <div class="wechat-copy">
              <strong>{{ t("login.wechatScanTitle") }}</strong>
              <span>{{ t("login.wechatScanHint") }}</span>
            </div>
            <div class="wechat-frame-shell">
              <div :id="wechatContainerId" class="wechat-login-container"></div>
            </div>
            <p v-if="errorMessage" class="form-error">{{ errorMessage }}</p>
          </div>

          <div v-if="buttonProviders.length" class="divider"><span>{{ t("login.chooseProvider") }}</span></div>
          <button
            v-for="provider in buttonProviders"
            :key="provider.key"
            class="secondary-button full-button"
            type="button"
            :disabled="providerLoading"
            @click="startExternalLogin(provider)"
          >
            <span>{{ provider.label }}</span>
          </button>
        </div>
      </section>
    </div>

    <SiteFooter />
  </main>
</template>
